import React, { useEffect, useMemo, useState } from 'react';
import ConstructionLayout from './ConstructionLayout';
import { useModule } from '../../contexts/ModuleContext';
import { useTheme } from '../../contexts/ThemeContext';
import { fetchMmmSummary } from '../../services/mmmService';
import {
  AlertCircle,
  Box,
  HardHat,
  Info,
  RefreshCw,
  Users,
} from 'lucide-react';
import { machineIconFor } from './MmmMachineIcons';

function formatCount(value) {
  const n = Number(value) || 0;
  return Number.isInteger(n)
    ? n.toLocaleString()
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatVolume(value) {
  return (Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatPct(share) {
  if (!Number.isFinite(share) || share <= 0) return '0%';
  return `${(share * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function KpiCard({ label, value, unit, icon: Icon, iconColor, cardBg, cardBorder, titleColor, muted }) {
  return (
    <div
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: '12px',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minHeight: '96px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: muted }}>{label}</div>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${iconColor}22`,
          }}
        >
          <Icon size={14} color={iconColor} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: 1.1,
            color: titleColor,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </span>
        {unit ? (
          <span style={{ fontSize: '13px', fontWeight: 600, color: muted }}>{unit}</span>
        ) : null}
      </div>
    </div>
  );
}

function PanelCard({ children, cardBg, cardBorder }) {
  return (
    <div
      style={{
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: '12px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        minHeight: '320px',
      }}
    >
      {children}
    </div>
  );
}

function PanelHeader({ icon: Icon, iconColor, title, subtitle, totalLabel, titleColor, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', gap: '10px', minWidth: 0 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${iconColor}22`,
            flexShrink: 0,
          }}
        >
          <Icon size={18} color={iconColor} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: titleColor }}>{title}</div>
          <div style={{ fontSize: '12px', color: muted, marginTop: 2 }}>{subtitle}</div>
        </div>
      </div>
      {totalLabel ? (
        <div
          style={{
            fontSize: '12px',
            fontWeight: 700,
            color: muted,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {totalLabel}
        </div>
      ) : null}
    </div>
  );
}

function ShareBar({ share, trackBg, fillColor }) {
  const width = Math.max(0, Math.min(100, (Number(share) || 0) * 100));
  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        backgroundColor: trackBg,
        overflow: 'hidden',
        width: '100%',
        minWidth: 64,
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${width}%`,
          backgroundColor: fillColor,
          borderRadius: 999,
        }}
      />
    </div>
  );
}

function ShareRow({
  icon: Icon,
  iconColor,
  label,
  valueLabel,
  share,
  text,
  muted,
  rowBorder,
  trackBg,
  barColor,
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.4fr) auto minmax(72px, 1fr) 44px',
        gap: '10px',
        padding: '8px 0',
        borderBottom: `1px solid ${rowBorder}`,
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${iconColor}18`,
            flexShrink: 0,
          }}
        >
          <Icon size={12} color={iconColor} />
        </div>
        <span
          title={label}
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          fontSize: '12px',
          fontWeight: 700,
          color: text,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          textAlign: 'right',
        }}
      >
        {valueLabel}
      </div>
      <ShareBar share={share} trackBg={trackBg} fillColor={barColor} />
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: muted,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatPct(share)}
      </span>
    </div>
  );
}

function ShareListHeader({ left, value, muted, cardBorder }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.4fr) auto minmax(72px, 1fr) 44px',
        gap: '10px',
        padding: '4px 0 6px',
        borderBottom: `1px solid ${cardBorder}`,
        fontSize: '10px',
        fontWeight: 600,
        color: muted,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        alignItems: 'center',
      }}
    >
      <span>{left}</span>
      <span style={{ textAlign: 'right' }}>{value}</span>
      <span style={{ gridColumn: '3 / -1', textAlign: 'right' }}>Share</span>
    </div>
  );
}

export default function MmmPage() {
  const { isDarkMode } = useTheme();
  const { activeProject, activeSurvey } = useModule();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const projectId = activeProject?.numericId;
  const surveyId = activeSurvey?.id;

  useEffect(() => {
    let isMounted = true;
    if (!projectId || !surveyId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMmmSummary(projectId, surveyId)
      .then((res) => {
        if (isMounted) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('Error fetching MMM summary:', err);
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [projectId, surveyId]);

  const cardBg = isDarkMode ? '#111622' : '#FFFFFF';
  const cardBorder = isDarkMode ? '#1E2433' : '#E5E7EB';
  const muted = isDarkMode ? '#9CA3AF' : '#6B7280';
  const text = isDarkMode ? '#E5E7EB' : '#1F2937';
  const titleColor = isDarkMode ? '#F3F4F6' : '#111827';
  const rowBorder = isDarkMode ? '#1E2433' : '#F3F4F6';
  const headBg = isDarkMode ? '#161B26' : '#F9FAFB';
  const trackBg = isDarkMode ? '#1E2433' : '#E5E7EB';
  const calloutBg = isDarkMode ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.1)';
  const calloutBorder = isDarkMode ? 'rgba(245, 158, 11, 0.35)' : 'rgba(217, 119, 6, 0.35)';

  const menColor = '#3B82F6';
  const machineColor = '#F59E0B';
  const materialColor = '#10B981';

  const men = data?.men || { totalCount: 0, items: [] };
  const machineItems = data?.machine?.items || [];
  const materialItems = data?.material?.items || [];

  const machineTotal = useMemo(
    () => machineItems.reduce((acc, row) => acc + (Number(row.count) || 0), 0),
    [machineItems]
  );
  const materialTotal = useMemo(
    () => materialItems.reduce((acc, row) => acc + (Number(row.volume) || 0), 0),
    [materialItems]
  );

  return (
    <ConstructionLayout
      title="Men, Machine & Material"
      subtitle="Key resources on site from aerial visual analysis."
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {!surveyId && (
          <div
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              backgroundColor: headBg,
              color: muted,
              fontSize: '13px',
            }}
          >
            Select a survey date to load Men, Machine & Material for this project.
          </div>
        )}

        {loading ? (
          <div
            style={{
              padding: '48px',
              textAlign: 'center',
              color: muted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            <RefreshCw className="animate-spin" size={18} /> Loading MMM summary…
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
              }}
            >
              <KpiCard
                label="Total Men"
                value={formatCount(men.totalCount)}
                icon={Users}
                iconColor={menColor}
                cardBg={cardBg}
                cardBorder={cardBorder}
                titleColor={titleColor}
                muted={muted}
              />
              <KpiCard
                label="Total Machines"
                value={formatCount(machineTotal)}
                icon={HardHat}
                iconColor={machineColor}
                cardBg={cardBg}
                cardBorder={cardBorder}
                titleColor={titleColor}
                muted={muted}
              />
              <KpiCard
                label="Total Material Volume"
                value={formatVolume(materialTotal)}
                unit="m³"
                icon={Box}
                iconColor={materialColor}
                cardBg={cardBg}
                cardBorder={cardBorder}
                titleColor={titleColor}
                muted={muted}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '16px',
              }}
            >
              <PanelCard cardBg={cardBg} cardBorder={cardBorder}>
                <PanelHeader
                  icon={Users}
                  iconColor={menColor}
                  title="Men"
                  subtitle="Estimated from aerial visual analysis"
                  titleColor={titleColor}
                  muted={muted}
                />

                {men.totalCount <= 0 && men.items.length === 0 ? (
                  <div style={{ fontSize: '13px', color: muted, padding: '24px 0' }}>No men data</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>
                    <div>
                      <div
                        style={{
                          fontSize: '44px',
                          fontWeight: 700,
                          lineHeight: 1,
                          color: titleColor,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatCount(men.totalCount)}
                      </div>
                      <div style={{ fontSize: '13px', color: muted, marginTop: 8 }}>
                        Men observed on site
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start',
                        padding: '12px',
                        borderRadius: '10px',
                        backgroundColor: calloutBg,
                        border: `1px solid ${calloutBorder}`,
                      }}
                    >
                      <AlertCircle size={16} color="#F59E0B" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div style={{ fontSize: '12px', lineHeight: 1.45, color: text }}>
                        Men count is estimated from aerial imagery and may vary due to visibility,
                        occlusion and site conditions.
                      </div>
                    </div>
                  </div>
                )}
              </PanelCard>

              <PanelCard cardBg={cardBg} cardBorder={cardBorder}>
                <PanelHeader
                  icon={HardHat}
                  iconColor={machineColor}
                  title="Machine"
                  subtitle="Machines and equipment identified"
                  totalLabel={`Total ${formatCount(machineTotal)}`}
                  titleColor={titleColor}
                  muted={muted}
                />

                {machineItems.length === 0 ? (
                  <div style={{ fontSize: '13px', color: muted, padding: '24px 0' }}>No machines</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <ShareListHeader left="Item" value="Count" muted={muted} cardBorder={cardBorder} />
                    {machineItems.map((row) => {
                      const count = Number(row.count) || 0;
                      const share = machineTotal > 0 ? count / machineTotal : 0;
                      const Icon = machineIconFor(row.item);
                      return (
                        <ShareRow
                          key={row.item}
                          icon={Icon}
                          iconColor={machineColor}
                          label={row.item}
                          valueLabel={formatCount(count)}
                          share={share}
                          text={text}
                          muted={muted}
                          rowBorder={rowBorder}
                          trackBg={trackBg}
                          barColor={machineColor}
                        />
                      );
                    })}
                  </div>
                )}
              </PanelCard>

              <PanelCard cardBg={cardBg} cardBorder={cardBorder}>
                <PanelHeader
                  icon={Box}
                  iconColor={materialColor}
                  title="Material"
                  subtitle="Stockpiles and material volumes (from 3D analysis)"
                  totalLabel={`Total ${formatVolume(materialTotal)} m³`}
                  titleColor={titleColor}
                  muted={muted}
                />

                {materialItems.length === 0 ? (
                  <div style={{ fontSize: '13px', color: muted, padding: '24px 0' }}>No materials</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <ShareListHeader left="Material" value="Volume (m³)" muted={muted} cardBorder={cardBorder} />
                    {materialItems.map((row) => {
                      const volume = Number(row.volume) || 0;
                      const share = materialTotal > 0 ? volume / materialTotal : 0;
                      return (
                        <ShareRow
                          key={row.item}
                          icon={Box}
                          iconColor={materialColor}
                          label={row.item}
                          valueLabel={formatVolume(volume)}
                          share={share}
                          text={text}
                          muted={muted}
                          rowBorder={rowBorder}
                          trackBg={trackBg}
                          barColor={materialColor}
                        />
                      );
                    })}
                  </div>
                )}
              </PanelCard>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                fontSize: '12px',
                color: muted,
                lineHeight: 1.45,
              }}
            >
              <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Data is derived from aerial visual analysis and AI model detection. Please validate
                critical quantities on site.
              </span>
            </div>
          </>
        )}
      </div>
    </ConstructionLayout>
  );
}
